import { useEffect, useMemo, useState } from "react";
import { PRODUCT_SHORT } from "../products";
import { api, ApiError, OrderRow, OrderInput, Acceptance } from "../api/client";
import { useToast } from "../ui/toast";
import { IconCopy, IconPlus, IconRefresh, IconSearch, IconX } from "../ui/icons";

const STATUSES: Record<string, string> = {
  new: "nowe",
  paid: "opłacone",
  fulfilled: "zrealizowane",
  cancelled: "anulowane",
};
const STATUS_BADGE: Record<string, string> = {
  new: "issued", paid: "expiring", fulfilled: "active", cancelled: "revoked",
};

/** Test order placed through the portal dev checkout (source e.g. "portal-dev"). */
const isDev = (o: OrderRow) => (o.source ?? "").toLowerCase().includes("dev");
/** True when the portal checkout stamped accepted document versions on the order. */
const hasPurchaseAcceptance = (o: OrderRow) =>
  !!(o.acceptedAt || o.acceptedTosVersion || o.acceptedPrivacyVersion || o.acceptedReturnsVersion);

function fmtDate(iso?: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pl-PL", { day: "2-digit", month: "short", year: "numeric" });
}
function fmtDateTime(iso?: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pl-PL", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function Orders() {
  const [rows, setRows] = useState<OrderRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState<OrderRow | null>(null);

  const load = () => {
    setRows(null);
    setError(null);
    api.listOrders()
      .then((r) => setRows(r.orders))
      .catch((e: ApiError) => setError(e.status === 403 ? "Brak dostępu (owner-only)." : e.message));
  };
  useEffect(load, []);

  const kpis = useMemo(() => {
    const r = rows ?? [];
    return {
      total: r.length,
      fulfilled: r.filter((o) => o.status === "fulfilled").length,
      awaiting: r.filter((o) => o.status === "new" || o.status === "paid").length,
      accepted: r.filter((o) => o.acceptance?.tos && o.acceptance?.privacy).length,
    };
  }, [rows]);

  const filtered = useMemo(() => {
    const r = rows ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return r;
    return r.filter((o) =>
      o.orderNumber.toLowerCase().includes(q) ||
      (o.company ?? "").toLowerCase().includes(q) ||
      (o.email ?? "").toLowerCase().includes(q) ||
      (o.nip ?? "").includes(q) ||
      (o.tid ?? "").includes(q));
  }, [rows, query]);

  return (
    <>
      <div className="kpi-grid" style={{ marginBottom: 22 }}>
        <Kpi label="Zamówienia" value={kpis.total} />
        <Kpi label="Oczekujące" value={kpis.awaiting} accent="medium" />
        <Kpi label="Zrealizowane" value={kpis.fulfilled} accent="accent" />
        <Kpi label="Zaakceptowane (ToS+prywatność)" value={kpis.accepted} accent="accent" />
      </div>

      <div className="row" style={{ marginBottom: 14 }}>
        <div style={{ position: "relative", flex: 1, maxWidth: 360 }}>
          <span style={{ position: "absolute", left: 11, top: 10, color: "var(--text-faint)" }}><IconSearch size={15} /></span>
          <input className="input" style={{ paddingLeft: 34 }} placeholder="Szukaj: nr / firma / e-mail / NIP / tenant…"
            value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className="topbar-spacer" />
        <button className="btn" onClick={load}><IconRefresh size={15} /> Odśwież</button>
        <button className="btn primary" onClick={() => setShowModal(true)}><IconPlus size={15} /> Nowe zamówienie</button>
      </div>

      <div className="card" style={{ overflow: "hidden" }}>
        {error ? (
          <div className="empty">{error}</div>
        ) : !rows ? (
          <div className="empty">Ładowanie…</div>
        ) : filtered.length === 0 ? (
          <div className="empty">Brak zamówień. Kliknij „Nowe zamówienie”, aby dodać pierwsze.</div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Nr</th><th>Klient</th><th>Plan</th><th>Status</th><th>Utworzono</th><th>Zgodność</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr key={o.rowKey} className={selected?.rowKey === o.rowKey ? "selected" : ""} onClick={() => setSelected(o)}>
                  <td className="mono">{o.orderNumber}{isDev(o) && <span className="badge st-expiring" style={{ marginLeft: 6 }}>DEV</span>}</td>
                  <td>
                    <div style={{ fontWeight: 550 }}>{o.company || o.email || <span className="faint">— bez opisu —</span>}</div>
                    <div className="faint" style={{ fontSize: 11 }}>{o.email && o.company ? o.email : o.nip || ""}</div>
                  </td>
                  <td>
                    {o.plan ? <span className={`badge ${o.plan === "governance" ? "st-active" : "st-issued"}`}>{PRODUCT_SHORT[o.plan]}</span> : <span className="faint">—</span>}
                    {o.periodMonths != null && <span className="faint" style={{ fontSize: 11, marginLeft: 6 }}>{o.periodMonths} mies.</span>}
                    {o.promoCode && <div className="faint mono" style={{ fontSize: 10.5 }}>{o.promoCode} −{o.discountPct}%</div>}
                  </td>
                  <td><span className={`badge st-${STATUS_BADGE[o.status] ?? "issued"}`}>{STATUSES[o.status] ?? o.status}</span></td>
                  <td className="tabular">{fmtDate(o.createdAt)}</td>
                  <td><AcceptancePills order={o} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && <NewOrderModal onClose={() => setShowModal(false)} onCreated={() => { setShowModal(false); load(); }} />}
      {selected && <OrderDrawer order={selected} onClose={() => setSelected(null)} onChanged={() => { setSelected(null); load(); }} />}
    </>
  );

  function Kpi({ label, value, accent }: { label: string; value: number; accent?: string }) {
    const color = accent === "medium" ? "var(--medium)" : accent === "critical" ? "var(--critical)" : accent === "accent" ? "var(--accent-text)" : "var(--text)";
    return (
      <div className="card kpi">
        <div className="kpi-label">{label}</div>
        <div className="kpi-value" style={{ color }}>{value}</div>
      </div>
    );
  }
}

/** Compact yes/no pills for ToS + privacy acceptance, shown in the list row. */
function AcceptancePills({ order }: { order: OrderRow }) {
  if (!order.tid) return <span className="faint" style={{ fontSize: 12 }}>brak tenanta</span>;
  const tos = !!order.acceptance?.tos;
  const priv = !!order.acceptance?.privacy;
  return (
    <div style={{ display: "flex", gap: 6 }}>
      <span className={`badge st-${tos ? "active" : "revoked"}`} title="Warunki licencji">Licencja {tos ? "✓" : "✗"}</span>
      <span className={`badge st-${priv ? "active" : "revoked"}`} title="Polityka prywatności">Prywatność {priv ? "✓" : "✗"}</span>
    </div>
  );
}

// ---- New order modal --------------------------------------------------------

function NewOrderModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const toast = useToast();
  const [f, setF] = useState<OrderInput>({ status: "new", plan: "standard", seats: 3, currency: "PLN" });
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<OrderInput>) => setF((prev) => ({ ...prev, ...patch }));

  const create = async () => {
    if (!f.company && !f.email) { toast("Podaj co najmniej firmę lub e-mail"); return; }
    setBusy(true);
    try {
      await api.createOrder(f);
      toast("Zamówienie utworzone");
      onCreated();
    } catch (e) { toast((e as ApiError).message); } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ padding: "18px 22px", borderBottom: "1px solid var(--border)" }}>
          <h3 style={{ fontSize: 16 }}>Nowe zamówienie</h3>
          <div className="topbar-spacer" />
          <button className="btn icon sm ghost" onClick={onClose}><IconX size={16} /></button>
        </div>
        <div style={{ padding: 22, maxHeight: "70vh", overflowY: "auto" }}>
          <div className="grid-2">
            <div className="field"><label className="label">Firma</label><input className="input" value={f.company ?? ""} onChange={(e) => set({ company: e.target.value })} placeholder="np. ACME sp. z o.o." /></div>
            <div className="field"><label className="label">E-mail</label><input className="input" value={f.email ?? ""} onChange={(e) => set({ email: e.target.value })} placeholder="kontakt@acme.pl" /></div>
          </div>
          <div className="grid-2">
            <div className="field"><label className="label">NIP</label><input className="input" value={f.nip ?? ""} onChange={(e) => set({ nip: e.target.value })} placeholder="000-000-00-00" /></div>
            <div className="field"><label className="label">Nr faktury (opcjonalnie)</label><input className="input" value={f.invoiceNumber ?? ""} onChange={(e) => set({ invoiceNumber: e.target.value })} /></div>
          </div>
          <div className="field">
            <label className="label">Produkt</label>
            <div className="segmented">
              <button className={f.plan === "standard" ? "active" : ""} onClick={() => set({ plan: "standard" })}>{PRODUCT_SHORT.standard}</button>
              <button className={f.plan === "governance" ? "active" : ""} onClick={() => set({ plan: "governance" })}>{PRODUCT_SHORT.governance}</button>
            </div>
          </div>
          <div className="grid-2">
            <div className="field"><label className="label">Miejsca</label><input className="input" type="number" min={1} value={f.seats ?? 1} onChange={(e) => set({ seats: Math.max(1, Number(e.target.value)) })} /></div>
            <div className="field"><label className="label">Kwota netto (opcjonalnie)</label><input className="input" type="number" min={0} value={f.amountNet ?? ""} onChange={(e) => set({ amountNet: e.target.value === "" ? undefined : Number(e.target.value) })} /></div>
          </div>
          <div className="field">
            <label className="label">Tenant (tid) — powiązanie do śledzenia akceptacji (opcjonalnie)</label>
            <input className="input mono" value={f.tid ?? ""} onChange={(e) => set({ tid: e.target.value })} placeholder="00000000-0000-0000-0000-000000000000" />
            <p className="faint" style={{ fontSize: 11, marginTop: 4 }}>Bez powiązanego tenanta karta zgodności pokaże „brak danych”. Tenant można też uzupełnić później.</p>
          </div>
          <div className="field"><label className="label">Notatki (opcjonalnie)</label><textarea className="textarea" style={{ minHeight: 70 }} value={f.notes ?? ""} onChange={(e) => set({ notes: e.target.value })} /></div>
        </div>
        <div className="row" style={{ padding: "16px 22px", borderTop: "1px solid var(--border)", justifyContent: "flex-end" }}>
          <button className="btn ghost" onClick={onClose}>Anuluj</button>
          <button className="btn primary" disabled={busy} onClick={create}>{busy ? "Tworzę…" : "Utwórz zamówienie"}</button>
        </div>
      </div>
    </div>
  );
}

// ---- Order drawer (details + compliance + edit) -----------------------------

function OrderDrawer({ order, onClose, onChanged }: { order: OrderRow; onClose: () => void; onChanged: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(order.status);
  const [tid, setTid] = useState(order.tid ?? "");
  const [invoiceNumber, setInvoiceNumber] = useState(order.invoiceNumber ?? "");
  const [confirmDel, setConfirmDel] = useState(false);

  const dirty = status !== order.status || tid !== (order.tid ?? "") || invoiceNumber !== (order.invoiceNumber ?? "");

  const save = async () => {
    setBusy(true);
    try {
      await api.updateOrder(order.rowKey, { status, tid, invoiceNumber });
      toast("Zapisano zmiany");
      onChanged();
    } catch (e) { toast((e as ApiError).message); } finally { setBusy(false); }
  };
  const del = async () => {
    setBusy(true);
    try { await api.deleteOrder(order.rowKey); toast("Zamówienie usunięte"); onChanged(); }
    catch (e) { toast((e as ApiError).message); } finally { setBusy(false); }
  };

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ padding: "18px 22px", borderBottom: "1px solid var(--border)" }}>
          <div>
            <h3 style={{ fontSize: 16 }}>Zamówienie {order.orderNumber}</h3>
            <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
              <span className={`badge st-${STATUS_BADGE[order.status] ?? "issued"}`}>{STATUSES[order.status] ?? order.status}</span>
              {isDev(order) && <span className="badge st-expiring">DEV (portal)</span>}
            </div>
          </div>
          <div className="topbar-spacer" />
          <button className="btn icon sm ghost" onClick={onClose}><IconX size={16} /></button>
        </div>

        <div style={{ padding: 22, overflowY: "auto", flex: 1 }}>
          <Detail label="Klient" value={order.company || "—"} />
          <Detail label="E-mail" value={order.email || "—"} />
          <Detail label="NIP" value={order.nip || "—"} />
          <Detail label="Produkt / miejsca" value={`${order.plan ? PRODUCT_SHORT[order.plan] : "—"}${order.seats ? ` · ${order.seats} miejsc` : ""}`} />
          {order.periodMonths != null && <Detail label="Okres licencji" value={`${order.periodMonths} mies.`} />}
          {order.amountNet != null && <Detail label="Kwota netto" value={`${order.amountNet} ${order.currency ?? "PLN"}${order.amountGross != null ? ` (brutto ${order.amountGross})` : ""}`} />}
          {order.promoCode && <Detail label="Kod promocyjny" value={`${order.promoCode} (−${order.discountPct}%) · cena katalogowa netto ${order.amountNetList ?? "—"} ${order.currency ?? "PLN"}`} />}
          {order.source && <Detail label="Źródło" value={order.source} />}
          {order.keyMasked && (
            <div style={{ marginBottom: 12 }}>
              <div className="faint" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>Klucz licencyjny (zamaskowany)</div>
              <div className="mono" style={{ fontSize: 13 }}>{order.keyMasked}</div>
              <div className="faint" style={{ fontSize: 11, marginTop: 2 }}>Pełny klucz nie jest przechowywany — wysłano go mailem przy zakupie (backend trzyma tylko hash).</div>
            </div>
          )}
          <Detail label="Utworzono" value={fmtDateTime(order.createdAt)} />

          {/* --- Akceptacja przy zakupie (portal checkout) --- */}
          {hasPurchaseAcceptance(order) && (
            <>
              <div style={{ marginTop: 18, marginBottom: 6, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-faint)" }}>
                Akceptacja przy zakupie (portal)
              </div>
              <div className="card" style={{ padding: 14 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <MiniDetail label="Warunki licencji" value={order.acceptedTosVersion ? `v${order.acceptedTosVersion} ✓` : "—"} />
                  <MiniDetail label="Polityka prywatności" value={order.acceptedPrivacyVersion ? `v${order.acceptedPrivacyVersion} ✓` : "—"} />
                  <MiniDetail label="Warunki zwrotu" value={order.acceptedReturnsVersion ? `v${order.acceptedReturnsVersion} ✓` : "—"} />
                  <MiniDetail label="Data akceptacji" value={fmtDateTime(order.acceptedAt)} />
                </div>
                <p className="faint" style={{ fontSize: 11, marginTop: 8 }}>
                  Zaakceptowane w momencie złożenia zamówienia w portalu (wymagane do przejścia do płatności).
                </p>
              </div>
            </>
          )}

          {/* --- Karta zgodności --- */}
          <div style={{ marginTop: 18, marginBottom: 6, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-faint)" }}>
            Zgodność — akceptacja warunków
          </div>
          {!order.tid ? (
            <div className="card" style={{ padding: 14 }}>
              <p className="faint" style={{ fontSize: 12.5 }}>
                Brak powiązanego tenanta. Uzupełnij „Tenant (tid)” niżej, aby śledzić akceptację warunków
                licencji i polityki prywatności przez tego klienta.
              </p>
            </div>
          ) : (
            <>
              <AcceptanceCard title="Warunki licencji" acc={order.acceptance?.tos ?? null} />
              <AcceptanceCard title="Polityka prywatności" acc={order.acceptance?.privacy ?? null} />
              <p className="faint" style={{ fontSize: 11, marginTop: 2 }}>
                Dane pochodzą z aplikacji klienta (raportowane przy akceptacji). Klient na starszej wersji
                bez raportowania pokaże „nie zaakceptowano”.
              </p>
            </>
          )}

          {/* --- Edycja --- */}
          <div style={{ marginTop: 20, marginBottom: 6, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-faint)" }}>
            Edycja
          </div>
          <div className="field">
            <label className="label">Status</label>
            <select className="input" value={status} onChange={(e) => setStatus(e.target.value)}>
              {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="label">Tenant (tid)</label>
            <input className="input mono" value={tid} onChange={(e) => setTid(e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" />
          </div>
          <div className="field">
            <label className="label">Nr faktury</label>
            <input className="input" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
          </div>
          {dirty && <button className="btn primary sm" disabled={busy} onClick={save}>Zapisz zmiany</button>}

          {order.tid && (
            <div style={{ marginTop: 12 }}>
              <button className="btn sm" onClick={() => { navigator.clipboard.writeText(order.tid!); toast("Skopiowano tid"); }}>
                <IconCopy size={14} /> Kopiuj tid
              </button>
            </div>
          )}
        </div>

        <div className="row" style={{ padding: "16px 22px", borderTop: "1px solid var(--border)", justifyContent: "flex-end" }}>
          {!confirmDel ? (
            <button className="btn" disabled={busy} onClick={() => setConfirmDel(true)}>Usuń zamówienie…</button>
          ) : (
            <button className="btn danger" disabled={busy} onClick={del}>Na pewno usunąć?</button>
          )}
        </div>
      </div>
    </div>
  );
}

/** One document's acceptance state: yes/no + version + first/last date. */
function AcceptanceCard({ title, acc }: { title: string; acc: Acceptance | null }) {
  const accepted = !!acc;
  return (
    <div className="card" style={{ padding: 14, marginBottom: 10 }}>
      <div className="row" style={{ marginBottom: accepted ? 8 : 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{title}</div>
        <div className="topbar-spacer" />
        <span className={`badge st-${accepted ? "active" : "revoked"}`}>
          <span className={`dot dot-${accepted ? "active" : "revoked"}`} />
          {accepted ? "zaakceptowano" : "nie zaakceptowano"}
        </span>
      </div>
      {accepted && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <MiniDetail label="Wersja" value={`v${acc!.version}`} />
          <MiniDetail label="Ostatnia akceptacja" value={fmtDateTime(acc!.lastAcceptedAt)} />
          <MiniDetail label="Pierwsza akceptacja" value={fmtDateTime(acc!.firstAcceptedAt)} />
          {acc!.appVersion && <MiniDetail label="Wersja aplikacji" value={acc!.appVersion} />}
        </div>
      )}
    </div>
  );
}

function MiniDetail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="faint" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      <div style={{ fontSize: 12.5 }}>{value}</div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div className="faint" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, wordBreak: "break-all" }}>{value}</div>
    </div>
  );
}
